/**
 * City registration warning on the job page (city registrations phase 1, PR 3).
 *
 * WARN ONLY, NEVER HOLD (Corbin, 2026-10-06). This card says what is missing
 * for this job's city — a tester not on file as registered, no current
 * insurance certificate for the city's holder, the city's own note (Plano's
 * $25 retest fee, Fort Worth dropping a registration when insurance lapses) —
 * and links to that city's packet. It never blocks dispatch, the test, the
 * filing or the invoice, and it renders nothing when there is nothing to say.
 *
 * It reads "Heads up", in blue with an ⓘ, not as a warning (2026-10-28,
 * #082669055): an orange card right under "This job can't go out yet" read
 * as the blocker, and Corbin went chasing an insurance certificate.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { InfoButton } from "@/components/info-button";
import { api } from "@/lib/api";
import { COMPLIANCE_COPY } from "@/pages/compliance/compliance-copy";

const J = COMPLIANCE_COPY.packets.jobCard;

interface WarningsResponse {
    providerId: string | null;
    city: string | null;
    warnings: Array<{ kind: string; userId: string | null; text: string }>;
}

export function RegistrationWarningsCard({ jobId }: { jobId: string }) {
    const q = useQuery({
        queryKey: ["job-registration-warnings", jobId],
        queryFn: () => api.get<WarningsResponse>(`/api/jobs/${jobId}/registration-warnings`),
        staleTime: 60_000,
        retry: false,
    });
    const d = q.data;
    if (!d || !d.city || d.warnings.length === 0) return null;
    return (
        <div
            className="flex flex-col gap-2 rounded-2xl border border-sky-300 bg-card p-4 dark:border-sky-800"
            data-testid="job-registration-warnings"
        >
            <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-sky-700 dark:text-sky-300" />
                <span className="text-sm font-bold text-foreground">{J.title(d.city)}</span>
                <InfoButton topic={J.info(d.city)} />
            </div>
            <ul className="flex flex-col gap-1">
                {d.warnings.map((w, i) => (
                    <li key={`${w.kind}-${w.userId ?? i}`} className="text-sm text-foreground">
                        {w.text}
                    </li>
                ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3">
                <Link
                    to={`/compliance/packets?open=${d.providerId}`}
                    className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
                >
                    {J.open(d.city)}
                </Link>
                <span className="text-xs text-muted-foreground">{J.warnOnly}</span>
            </div>
        </div>
    );
}
